import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useParentAccess } from "@/hooks/useParentAccess";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ChevronRight, UserCog } from "lucide-react";
import Navbar from "@/components/Navbar";

interface Props {
  children: ReactNode;
  /** Si vrai, affiche juste un bandeau d'information sans bloquer les enfants */
  softWarningOnly?: boolean;
}

/**
 * Bloque l'accès des fonctionnalités (recherche, réservation, messagerie…)
 * tant qu'un parent n'a pas renseigné nom, téléphone, adresse, ville et bio.
 * Les visiteurs non connectés ou les autres rôles passent à travers.
 */
const ParentAccessGate = ({ children, softWarningOnly = false }: Props) => {
  const navigate = useNavigate();
  const { status, missingFields } = useParentAccess();

  if (status === "loading" || status === "not_parent" || status === "complete") {
    return <>{children}</>;
  }

  if (softWarningOnly) {
    return (
      <>
        <div className="max-w-3xl mx-auto px-4 mt-4">
          <Card className="border-amber-300/60 bg-amber-50/70 dark:bg-amber-950/30">
            <CardContent className="py-3 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
              <p className="text-sm flex-1">
                Complétez votre profil parent pour profiter pleinement de Click&Gard.
              </p>
              <Button size="sm" onClick={() => navigate("/settings?tab=profile")}>
                Compléter
              </Button>
            </CardContent>
          </Card>
        </div>
        {children}
      </>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-xl mx-auto px-4 py-12">
        <Card className="border-amber-300/60 shadow-lg">
          <CardContent className="py-8 space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center shrink-0">
                <UserCog className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold">Complétez votre profil parent</h2>
                <p className="text-sm text-muted-foreground">
                  Quelques informations sont requises pour accéder à cette fonctionnalité.
                </p>
              </div>
            </div>

            <div className="rounded-xl bg-amber-50/70 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-4">
              <p className="text-xs font-semibold text-amber-800 dark:text-amber-300 mb-3">
                Champs manquants ({missingFields.length})
              </p>
              <div className="flex flex-wrap gap-2">
                {missingFields.map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => navigate(`/settings?tab=profile&focus=${f.fieldId}`)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white dark:bg-amber-900/40 border border-amber-300 dark:border-amber-700 text-xs font-medium text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/70 transition-colors"
                  >
                    {f.label}
                    <ChevronRight className="w-3 h-3" />
                  </button>
                ))}
              </div>
            </div>

            <p className="text-xs text-muted-foreground leading-relaxed">
              Exemple de bio :{" "}
              <em>
                « Bonjour je m'appelle Eva, maman de Enzo 5 ans et Clara 3 ans, il m'arrive de
                rechercher des baby-sitter pour garder mes deux trésors. »
              </em>
            </p>

            <Button
              className="w-full rounded-xl"
              onClick={() => navigate("/settings?tab=profile")}
            >
              Compléter mon profil
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ParentAccessGate;
